import React, { useState, useEffect } from 'react';
import { Dialog } from '@material-ui/core';
import { Button } from '../../Button';
import useStyles from './styles';
import { get, size, first, orderBy } from 'global/lib/lodash';
import clsx from 'clsx';
import colors from '../../../styles/colors';
import { executePrint, exportToCsv, getRealmName, sortAlphaNum, sortByOrderKey, wait } from '../../../utils';
import { useView } from '../../../hooks/useView';
import useBodyOverflow from 'global/hooks/useBodyOverflow';
import sendData from 'redux/actions/sendData';
import moment from 'moment';
import * as arrayToTree from 'array-to-tree';
import { getItemType, isGob } from '../../../../feature/report/AssessmentScore/utils';

const PrintRow = (props) => {
  const { li, first } = props;

  const classes = useStyles();

  return (
    <>
      {!Object.keys(li).length ? (
        <></>
      ) : (
        <div className={first ? classes.pagebreakfirst : classes.pagebreak}>
          {!!li.title && <div style={get(li, 'titleStyle', {})}>{li.title}</div>}
          <div style={li.style}>{li.render({ onLoaded: props.onLoaded })}</div>
        </div>
      )}
    </>
  );
};

export const PrintView = (props) => {
  //items come over as { render: () => {}, style: {{}} }
  const { items, title, button, onClose, assessment } = props;

  const { view, toggleTheme } = useView();
  const [open, setOpen] = useState(false);
  const [toggleBack, setToggleBack] = useState(false);
  const [loaded, setLoaded] = useState({});
  const { setBodyOverflow } = useBodyOverflow();
  const [exp, setExp] = useState({
    loading: false,
  });

  const classes = useStyles();

  const toggleRoot = (hide = true) => {
    const root = document.getElementById('root');
    root.style.display = hide ? 'none' : 'block';
    if (!hide) {
      setBodyOverflow();
    }
  };

  const theme = get(view, 'style.palette.type', 'light');

  useEffect(() => {
    toggleRoot(open);
    if (open && theme === 'dark') {
      toggleTheme();
      setToggleBack(true);
    } else if (!open && toggleBack) {
      toggleTheme();
      setToggleBack(false);
    }
    if (!open && onClose) {
      onClose();
    }
    if (!open) {
      setLoaded({});
    }
  }, [open]);

  const onPrint = () => {
    if (open) {
      executePrint();
    } else {
      setOpen(true);
    }
  };

  const onExport = async () => {
    try {
      setExp((s) => ({ ...s, loading: true }));

      const _id = get(assessment, 'id');

      const scdata = await Promise.all(
        get(assessment, 'scorecards', []).map(
          async (scard) =>
            await Promise.all([
              sendData(
                { url: `/processed-scorecard-score-values` },
                {
                  normalize: { build: ['processedScorecardScoreValues'] },
                  params: {
                    filter: { assessment_id: _id, scorecard_id: get(scard, 'id') },
                    include: 'checklist',
                  },
                }
              ),
              first(
                await sendData(
                  { url: `/scorecards/${scard.id}` },
                  {
                    normalize: { build: ['scorecards'] },
                    params: {
                      include: 'checklists,checklists.images,checklists.checkpoints,checklists.checkpoints.scorecardOptions',
                    },
                  }
                )
              ),
            ])
        )
      );

      const createRow = (arr = []) => {
        const ret = [];
        for (let i = 0; i < 6; i++) {
          ret.push(arr[i] !== undefined ? arr[i] : '');
        }
        return ret;
      };

      const itemSeperator = ' | ';

      const aname = assessment.name;

      const rows = [
        createRow([aname]),
        createRow(),
        createRow(['Criteria', scdata.map((s) => get(s, '1.name')).join(itemSeperator)]),
        createRow(['Location', getRealmName(assessment, '', true)]),
        createRow(['Author', get(assessment, 'authors.0.name')]),
        createRow(['Editors', assessment.editors.map((a) => a.name).join(itemSeperator)]),
        createRow(['Viewers', assessment.viewers.map((a) => a.name).join(itemSeperator)]),
        createRow(['Created At', moment(get(assessment, 'createdAt')).format('L')]),
        createRow(['Downloaded At', moment().format('L')]),
      ];

      const loopRows = (r, parents = []) => {
        const type = getItemType(r);
        const isgob = isGob(r);

        if (type === 'superset') {
          rows.push(createRow([r.name, 'OVERALL', '', '', '', r.displayScore]));
        } else if (type === 'checklist') {
          rows.push(createRow([get(parents, '0.name'), r.name, 'OVERALL', '', '', r.displayScore]));
        } else if (isgob) {
          rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), 'GENERAL OBSERVATIONS', '', get(r, 'criteria', ''), r.displayScore]));
        } else if (type === 'checkpoint') {
          rows.push(createRow([get(parents, '0.name'), get(parents, '1.name'), r.name, get(r, 'criteria', ''), get(r, 'notes', ''), r.displayScore]));
        }

        if (r.children) {
          const childOrder =
            type === 'checklist'
              ? r.children.sort((a, b) => {
                  if (!a.checkpointId) {
                    return -1;
                  }
                  if (!b.checkpointId) {
                    return 1;
                  }
                  return sortAlphaNum(a, b);
                })
              : orderBy(r.children, 'order');
          childOrder.map((c) => loopRows(c, [...parents, r]));
        }
      };

      scdata.map((sc) => {
        rows.push(createRow());

        const [scores, scorecard] = sc;
        const treed = arrayToTree(scores, { parentProperty: 'parentId' });

        rows.push(createRow([scorecard.name]));
        rows.push(createRow(['System', 'Checklist', 'Checkpoint', 'Description', 'Notes', 'Score']));

        treed.map((t) => {
          orderBy(t.children, 'order').map((c) => loopRows(c));
        });
      });

      exportToCsv(`${new Date().toISOString()}_${aname}_export.csv`, rows);
    } catch (err) {
      alert(err);
    } finally {
      setExp((s) => ({ ...s, loading: false }));
    }
  };

  const extraClick = (e) => {
    get(button, 'onClick', () => 0)(e);
    onPrint(e);
  };

  const onLoaded = (li, i) => {
    setLoaded((l) => ({ ...l, [i]: true }));
  };

  const renderButtons = () => {
    const printText = get(props, 'printBtn.text', open ? 'Print' : `Print ${size(items)}`);
    const exportBtn = get(props, 'exportBtn');
    const printStyle = get(props, 'printBtn.style', {});
    const useLoaded = get(props, 'useLoaded', false);
    const loadComplete = Object.keys(loaded).length >= size(items);

    const disabled = !useLoaded ? false : open ? !loadComplete : false;

    return !size(items) ? null : (
      <div style={!open ? props.buttonContainerStyle : {}} className={clsx(classes.buttonContainer, open && classes.buttonContainerFixed)}>
        {button && !open && (
          <Button onClick={extraClick} className={clsx(classes.button, classes.extraButton)}>
            {get(button, 'title', 'Print All')}
          </Button>
        )}
        {open && (
          <Button className={classes.button} bgColor={colors.red} onClick={() => setOpen(false)}>
            {'Close'}
          </Button>
        )}
        {!!exportBtn && !open && (
          <Button onClick={onExport} loading={exp.loading} style={get(exportBtn, 'style', {})} className={classes.button}>
            {get(exportBtn, 'text')}
          </Button>
        )}
        <Button className={classes.button} style={open ? {} : printStyle} disabled={disabled} onClick={onPrint}>
          {printText}
        </Button>
      </div>
    );
  };

  const hideCoverPage = get(props, 'hideCoverPage', false);

  return (
    <>
      <Dialog className={classes.dialog} fullScreen={true} open={open}>
        <div className={classes.fauxPage}>
          {!hideCoverPage && (
            <div className={clsx(classes.contents)}>
              <div className={classes.title}>{title}</div>
              {items.map((item, i) => {
                return <div className={classes.coverTitle}>{item.coverTitle}</div>;
              })}
            </div>
          )}

          {items.map((li, i) => (
            <PrintRow key={i} first={i == 0 && hideCoverPage ? true : false} li={li} onLoaded={() => onLoaded(li, i)} />
          ))}
        </div>

        {renderButtons()}
      </Dialog>

      {renderButtons()}
    </>
  );
};
